import { z } from 'zod';

export const ONBOARDING_AREAS = [
  'school',
  'french',
  'sport',
  'youtube',
  'novel',
  'sleep',
  'personal'
] as const;

export const onboardingAreaSchema = z.enum(ONBOARDING_AREAS);

const timeSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Bitte gib eine gültige Uhrzeit ein.');

const goalNumber = (label: string, maximum: number, minimum = 1) =>
  z
    .number({ error: `${label} muss eine Zahl sein.` })
    .int(`${label} muss eine ganze Zahl sein.`)
    .min(minimum, `${label} muss mindestens ${minimum} sein.`)
    .max(maximum, `${label} darf höchstens ${maximum} sein.`);

export const onboardingSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(2, 'Bitte gib mindestens 2 Zeichen ein.')
      .max(50, 'Der Name darf höchstens 50 Zeichen lang sein.'),
    primaryGoal: z
      .string()
      .trim()
      .min(3, 'Beschreibe dein wichtigstes Ziel in mindestens 3 Zeichen.')
      .max(160, 'Das Ziel darf höchstens 160 Zeichen lang sein.'),
    wakeTime: timeSchema,
    sleepTime: timeSchema,
    areas: z
      .array(onboardingAreaSchema)
      .min(1, 'Wähle mindestens einen Bereich aus.')
      .max(ONBOARDING_AREAS.length),
    dailyGoals: z.object({
      schoolMinutes: goalNumber('Die Schulzeit', 720),
      frenchMinutes: goalNumber('Die Französischzeit', 720),
      youtubeMinutes: goalNumber('Die YouTube-Zeit', 720),
      novelChapters: goalNumber('Das Kapitelziel', 100),
      pushUpCurrentMax: goalNumber('Das aktuelle Maximum', 1_000, 0),
      pushUpLongTermGoal: goalNumber('Das langfristige Ziel', 2_000),
      squatsDaily: goalNumber('Das Kniebeugenziel', 2_000)
    })
  })
  .superRefine(({ dailyGoals }, context) => {
    if (dailyGoals.pushUpLongTermGoal < dailyGoals.pushUpCurrentMax) {
      context.addIssue({
        code: 'custom',
        message: 'Das langfristige Ziel darf nicht unter deinem aktuellen Maximum liegen.',
        path: ['dailyGoals', 'pushUpLongTermGoal']
      });
    }
  });

/** Fully validated data emitted when onboarding is completed. */
export type OnboardingData = z.infer<typeof onboardingSchema>;
export type OnboardingArea = z.infer<typeof onboardingAreaSchema>;
export type DailyGoals = OnboardingData['dailyGoals'];

export type OnboardingInitialData = Partial<Omit<OnboardingData, 'areas' | 'dailyGoals'>> & {
  areas?: OnboardingArea[];
  dailyGoals?: Partial<DailyGoals>;
};

export const DEFAULT_ONBOARDING_DATA: OnboardingData = {
  name: 'Yousif',
  primaryGoal: '',
  wakeTime: '08:00',
  sleepTime: '00:00',
  areas: [...ONBOARDING_AREAS],
  dailyGoals: {
    schoolMinutes: 120,
    frenchMinutes: 90,
    youtubeMinutes: 60,
    novelChapters: 8,
    pushUpCurrentMax: 40,
    pushUpLongTermGoal: 200,
    squatsDaily: 100
  }
};

export function createOnboardingDefaults(initialData?: OnboardingInitialData): OnboardingData {
  return {
    ...DEFAULT_ONBOARDING_DATA,
    ...initialData,
    areas: initialData?.areas ? [...initialData.areas] : [...DEFAULT_ONBOARDING_DATA.areas],
    dailyGoals: {
      ...DEFAULT_ONBOARDING_DATA.dailyGoals,
      ...initialData?.dailyGoals
    }
  };
}

export function validateOnboardingData(input: unknown): OnboardingData {
  return onboardingSchema.parse(input);
}
